<template>
  <section>
    <RevButton
      v-if="shouldShowClearButton"
      class="mb-32"
      :icon="IconCross"
      size="small"
      variant="primary"
      @click="handleClearAllFacets"
    >
      {{ i18n(translations.clearAll) }}
    </RevButton>
    <RevAccordionList :hasExternalBorders="false">
      <RevAccordionItem v-for="(facet, facetName) in facets" :key="facetName">
        <template #title>
          <h3 class="body-1-bold first-letter:capitalize">
            {{ formatTitle(facetName) }}
          </h3>
        </template>

        <template
          #description
          v-if="shouldShowDescriptionForFacetName(facetName)"
        >
          {{ getStringifiedValueForFacetName(facetName) }}
        </template>

        <template #body>
          <Facet
            :facet
            :name="facetName"
            :value="getValueForFacetName(facetName)"
            @change="handleFacetValueChange"
          />
        </template>
      </RevAccordionItem>
    </RevAccordionList>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { FacetsType } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-products.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevAccordionList } from '@ds/components/AccordionList'
import { RevButton } from '@ds/components/Button'
import { IconCross } from '@ds/icons/IconCross'

import Facet from '~/scopes/algolia/components/Facet/Facet.vue'

import { FACET_NAME_PREFIX, MODEL_EVENT } from './Facets.constants'
import translations from './Facets.translations'

const props = withDefaults(
  defineProps<{
    facets: FacetsType
    modelValue?: Record<string, string[] | string>
    formatTitleRule?: {
      separator: string
    }
  }>(),
  {
    modelValue: () => ({}),
    formatTitleRule: undefined,
  },
)

const emit = defineEmits([MODEL_EVENT])

const i18n = useI18n()

const shouldShowClearButton = computed(() => !isEmpty(props.modelValue))

function formatTitle(facetName: string) {
  if (!isEmpty(props.formatTitleRule)) {
    const splitByDots = facetName.split(props.formatTitleRule.separator)

    return [...splitByDots].pop()
  }

  return facetName
}

function getValueForFacetName(facetName: string) {
  return props.modelValue[`${FACET_NAME_PREFIX}${facetName}`]
}

function getStringifiedValueForFacetName(facetName: string) {
  const value = getValueForFacetName(facetName)

  return Array.isArray(value) ? value.join(', ') : value
}

function shouldShowDescriptionForFacetName(facetName: string) {
  const value = getValueForFacetName(facetName)

  return !isEmpty(value)
}

function handleClearAllFacets() {
  emit(MODEL_EVENT, {})
}

function handleFacetValueChange(
  facetName: string,
  valueForFacetName: string[],
) {
  const key = `${FACET_NAME_PREFIX}${facetName}`
  const newFacetsValues = { ...props.modelValue }
  if (valueForFacetName.length > 0) {
    newFacetsValues[key] = valueForFacetName
  } else {
    delete newFacetsValues[key]
  }
  emit(MODEL_EVENT, newFacetsValues)
}
</script>
